import { widgetSrc } from "./product";

/** Widget loader defaults. Colours mirror tokens.css brand values (brand-500 / ink-950). */
export const WIDGET_DEFAULTS = {
  primary: "#00aeef",
  onPrimary: "#080c1c",
  radiusPx: 14,
  launcherSizePx: 56,
  zIndex: 2147483000,
} as const;

/** Tenant theming custom properties (CLAUDE.md rule 3). */
export const TENANT_THEME_VARS = ["--wg-primary", "--wg-on-primary", "--wg-radius", "--wg-font"] as const;
export type TenantThemeVar = (typeof TENANT_THEME_VARS)[number];

const ON_DARK = "#ffffff";
const AA_CONTRAST = 4.5;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of `#rrggbb` (or `#rgb`); null for invalid input. */
export function relativeLuminance(hex: string): number | null {
  const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) return null;
  const digits = match[1].length === 3 ? match[1].replace(/./g, (d) => d + d) : match[1];
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
  return 0.2126 * channel(r ?? 0) + 0.7152 * channel(g ?? 0) + 0.0722 * channel(b ?? 0);
}

export function contrastRatio(a: number, b: number): number {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/** Auto-contrast text for a tenant colour (PLAN §2): navy if it reaches 4.5:1, else white. */
export function onColorFor(hex: string): string {
  const luminance = relativeLuminance(hex);
  if (luminance === null) return WIDGET_DEFAULTS.onPrimary;
  const navy = relativeLuminance(WIDGET_DEFAULTS.onPrimary) ?? 0;
  return contrastRatio(luminance, navy) >= AA_CONTRAST ? WIDGET_DEFAULTS.onPrimary : ON_DARK;
}

export const WIDGET_SCRIPT_ATTRIBUTES = {
  agent: "data-agent",
  mode: "data-mode",
} as const;

export const WIDGET_EVENT_PREFIX = "sagot:";
export const WIDGET_PANEL_VERSION = "0.0.0";

/** Install snippet (DEC-011 §2 as updated by DEC-040: `app.<root>/widget.js`, `data-position`, `defer`). */
export function widgetEmbedSnippet(agentKey: string, position: "right" | "left" = "right", src: string = widgetSrc()): string {
  return `<script src="${src}" data-agent="${agentKey}" data-position="${position}" defer></script>`;
}
